import { Component, OnDestroy, OnInit } from '@angular/core';
import {
  ApprovalStatus,
  Engagements,
  ExecutionStatus,
  GetQueryRecordsQuery,
  GetQueryRecordsQueryVariables,
  OnExecutionUpdateQueryRecordsSubscription,
  OnExecutionUpdateQueryRecordsSubscriptionVariables,
  OnUpdateQueryRecordsSubscription,
  OnUpdateQueryRecordsSubscriptionVariables,
  OnValidateQueryRecordsSubscription,
  OnValidateQueryRecordsSubscriptionVariables,
  QueryRecord,
  Status,
  SyntheticResultStatus,
} from '../../../../API';
import { ApiService } from '../../../shared/services/api.service';
import { AppService } from '../../../shared/services/app.service';
import { GetQueryRecords } from '../../../../graphql/queries';
import { NzMessageService } from 'ng-zorro-antd/message';
import {
  onExecutionUpdateQueryRecords,
  onUpdateQueryRecords,
  onValidateQueryRecords,
} from '../../../../graphql/subscriptions';
import { distinctUntilChanged, Subject, Subscription, takeUntil } from 'rxjs';

@Component({
  selector: 'app-query-history',
  templateUrl: './query-history.component.html',
  styleUrl: './query-history.component.scss',
})
export class QueryHistoryComponent implements OnInit, OnDestroy {
  queryRecords: QueryRecord[] = [];
  isLoading?: boolean;
  lastModifiedByFilter: { text: string; value: string }[] = [];
  headers: any[] = [];
  engagement: Engagements | null = null;
  graphqlSubscription?: Subscription;
  destroy$ = new Subject<void>();
  graphqlValidationSubscription?: Subscription;
  graphqlMutationSubscription?: Subscription;

  constructor(
    private apiService: ApiService,
    private appService: AppService,
    private nzMessageService: NzMessageService,
  ) {}

  ngOnInit(): void {
    this.appService.activeEngagement$
      .pipe(
        takeUntil(this.destroy$),
        distinctUntilChanged((prev, curr) => prev.engagement !== curr.engagement),
      )
      .subscribe((engagement: Engagements) => {
        this.engagement = engagement;
        this.getQueryRecords(engagement.engagement);

        this.listenRealtimeChanges(engagement.engagement);
        this.listenRealTimeValidationChanges(engagement.engagement);
        this.listenRealtimeUpdateMutationChanges(engagement.engagement);
      });
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
    this.graphqlSubscription?.unsubscribe();
    this.graphqlValidationSubscription?.unsubscribe();
    this.graphqlMutationSubscription?.unsubscribe();
  }

  setHeaders(): void {
    this.headers = [
      {
        name: 'Query ID',
        sortOrder: null,
        sortFn: (a: QueryRecord, b: QueryRecord) => a.queryId?.localeCompare(b.queryId ?? ''),
        sortDirections: ['ascend', 'descend', null],
        left: true,
      },
      {
        name: 'Name',
        sortOrder: null,
        sortFn: (a: QueryRecord, b: QueryRecord) => a.name?.localeCompare(b.name ?? ''),
        sortDirections: ['ascend', 'descend', null],
      },
      {
        name: 'Description',
        width: '10rem',
      },
      {
        name: 'Type',
        sortOrder: null,
        sortFn: (a: QueryRecord, b: QueryRecord) => a.type?.localeCompare(b.type ?? ''),
        sortDirections: ['ascend', 'descend', null],
        listOfFilter: [
          { text: 'Aggregate', value: 'AGGREGATE' },
          { text: 'List', value: 'LIST' },
        ],
        filterFn: (type: string, queryRecord: QueryRecord) => queryRecord.type?.indexOf(type) !== -1,
        width: '6rem',
      },
      {
        name: 'Query',
        width: '15rem',
      },
      {
        name: 'Last Modified By',
        key: 'lastModifiedBy',
        listOfFilter: this.lastModifiedByFilter,
        filterFn: (lastModifiedByList: string[], queryRecord: QueryRecord) =>
          lastModifiedByList.some(
            (lastModifiedBy) => queryRecord.lastModifiedBy && queryRecord.lastModifiedBy.indexOf(lastModifiedBy) !== -1,
          ),
        filterMultiple: true,
        width: '13rem',
      },
      {
        name: 'Last Modified At',
        sortOrder: 'descend',
        sortFn: (a: QueryRecord, b: QueryRecord) =>
          new Date(a.lastModifiedAt!).getTime() - new Date(b.lastModifiedAt!).getTime(),
        sortDirections: ['ascend', 'descend', null],
        width: '14rem',
      },
      {
        name: 'Validation Status',
        sortOrder: null,
        sortFn: (a: QueryRecord, b: QueryRecord) => a.validationStatus?.localeCompare(b.validationStatus ?? ''),
        sortDirections: ['ascend', 'descend', null],
        listOfFilter: [
          { text: 'Success', value: Status.SUCCESS },
          { text: 'Fail', value: Status.FAIL },
        ],
        filterFn: (validationStatus: string, queryRecord: QueryRecord) =>
          queryRecord.validationStatus && queryRecord.validationStatus.indexOf(validationStatus) !== -1,
        width: '10rem',
      },
      {
        name: 'Synthetic Status',
        sortOrder: null,
        sortFn: (a: QueryRecord, b: QueryRecord) => a.syntheticResult?.localeCompare(b.syntheticResult ?? ''),
        sortDirections: ['ascend', 'descend', null],
        listOfFilter: [
          { text: 'Success', value: SyntheticResultStatus.SUCCESS },
          { text: 'Fail', value: SyntheticResultStatus.FAIL },
          { text: 'IN Progress', value: SyntheticResultStatus.IN_PROGRESS },
          { text: 'CLEANED', value: SyntheticResultStatus.CLEANED },
        ],
        filterFn: (syntheticStatus: string, queryRecord: QueryRecord) =>
          queryRecord.syntheticResult && queryRecord.syntheticResult.indexOf(syntheticStatus) !== -1,
        width: '10rem',
      },
      {
        name: 'Approval Status',
        sortOrder: null,
        sortFn: (a: QueryRecord, b: QueryRecord) => a.approvalStatus?.localeCompare(b.approvalStatus ?? ''),
        sortDirections: ['ascend', 'descend', null],
        listOfFilter: [
          { text: 'Approved', value: ApprovalStatus.APPROVED },
          { text: 'Pending', value: ApprovalStatus.PENDING },
          { text: 'Rejected', value: ApprovalStatus.REJECTED },
        ],
        filterFn: (approvalStatuses: string[], queryRecord: QueryRecord) =>
          approvalStatuses.some(
            (approvalStatus) => queryRecord.approvalStatus && queryRecord.approvalStatus.indexOf(approvalStatus) !== -1,
          ),
        filterMultiple: true,
        width: '10rem',
      },
      {
        name: 'Rejection Reason',
      },
      {
        name: 'Execution Status',
        sortOrder: null,
        sortFn: (a: QueryRecord, b: QueryRecord) => a.executionStatus?.localeCompare(b.executionStatus ?? ''),
        sortDirections: ['ascend', 'descend', null],
        listOfFilter: [
          { text: 'Success', value: ExecutionStatus.SUCCESS },
          { text: 'Fail', value: ExecutionStatus.FAIL },
          { text: 'IN Progress', value: ExecutionStatus.IN_PROGRESS },
        ],
        filterFn: (executionStatus: string, queryRecord: QueryRecord) =>
          queryRecord.executionStatus && queryRecord.executionStatus.indexOf(executionStatus) !== -1,
        width: '10rem',
      },
      {
        name: 'Execution Duration',
        width: '10rem',
      },
      {
        name: 'Result',
        width: '5rem',
      },
      {
        name: 'Action',
        width: '4rem',
        right: true,
      },
    ];
  }

  getQueryRecords(engagementId: string): void {
    this.isLoading = true;
    this.apiService
      .query<GetQueryRecordsQuery, GetQueryRecordsQueryVariables>(GetQueryRecords, {
        engagementId,
      })
      .then((response) => {
        this.setHeaders();
        this.queryRecords = response.data?.GetQueryRecords as QueryRecord[];
        this.setLastModifiedByFilter();
      })
      .catch((error) => {
        this.nzMessageService.error(
          `Failed to fetch query records because of ${error.errors[0].errorType} with message: ${error.errors[0].message}`,
        );
      })
      .finally(() => (this.isLoading = false));
  }

  setLastModifiedByFilter(): void {
    this.headers.find((head) => head.key === 'lastModifiedBy').listOfFilter = this.queryRecords
      .map((record: any) => record.lastModifiedBy)
      .filter((lastModifiedBy, index, arr) => arr.indexOf(lastModifiedBy) === index)
      .map((lastModifiedBy) => {
        return {
          text: lastModifiedBy,
          value: lastModifiedBy,
        };
      });
  }

  reloadQueryHistory(): void {
    this.getQueryRecords(this.engagement!.engagement!);
  }

  listenRealtimeChanges(engagement: string): void {
    this.graphqlSubscription = this.apiService
      .subscription<OnExecutionUpdateQueryRecordsSubscription, OnExecutionUpdateQueryRecordsSubscriptionVariables>(
        onExecutionUpdateQueryRecords,
        {
          engagement,
        },
      )
      .subscribe({
        next: (value) => {
          const updatedItem = value?.data?.onExecutionUpdateQueryRecords;
          if (updatedItem) {
            this.updateItemInList(updatedItem);
          }
        },
        error: (error: any) => console.warn('Subscription error:', error),
      }) as Subscription;
  }

  updateItemInList(updatedItem: QueryRecord): void {
    this.queryRecords = this.queryRecords.map((item) => {
      if (item.queryId === updatedItem.queryId) {
        if (item.executionStatus !== updatedItem.executionStatus) {
          this.announceStateUpdate(updatedItem);
        }
        if (item.syntheticResult !== updatedItem.syntheticResult) {
          this.announceSyntheticUpdate(updatedItem);
        }
        return updatedItem;
      }
      return item;
    });
  }

  listenRealtimeUpdateMutationChanges(engagement: string): void {
    this.graphqlMutationSubscription = this.apiService
      .subscription<OnUpdateQueryRecordsSubscription, OnUpdateQueryRecordsSubscriptionVariables>(onUpdateQueryRecords, {
        engagement,
      })
      .subscribe({
        next: (value) => {
          const updatedItem = value?.data?.onUpdateQueryRecords;
          if (updatedItem) {
            this.updateItemInList(updatedItem);
          }
        },
        error: (error: any) => console.warn('Subscription error:', error),
      }) as Subscription;
  }

  announceStateUpdate(newItem: QueryRecord): void {
    const { name: queryName, executionStatus } = newItem;
    let msg = '';
    switch (executionStatus) {
      case ExecutionStatus.IN_PROGRESS: {
        msg = `Execution of Query ${queryName} is now in progress.`;
        break;
      }

      case ExecutionStatus.SUCCESS: {
        msg = `Execution of Query ${queryName} is completed successfully.`;
        break;
      }

      case ExecutionStatus.FAIL: {
        msg = `Execution of Query ${queryName} is failed.`;
        break;
      }
    }
    this.nzMessageService.info(msg, {
      nzDuration: 5000,
    });
  }

  listenRealTimeValidationChanges(engagement: string): void {
    this.graphqlValidationSubscription = this.apiService
      .subscription<OnValidateQueryRecordsSubscription, OnValidateQueryRecordsSubscriptionVariables>(
        onValidateQueryRecords,
        { engagement },
      )
      .subscribe({
        next: (value) => {
          const updatedItem = value?.data?.onValidateQueryRecords;
          if (updatedItem && updatedItem.validationStatus && updatedItem.validationStatus !== Status.IN_PROGRESS) {
            this.updateStatusItem(updatedItem);
          }
        },
        error: (error: any) => {
          console.warn('Subscription error: ', error);
        },
      }) as Subscription;
  }

  updateStatusItem(updatedItem: QueryRecord): void {
    this.queryRecords = this.queryRecords.map((item) => {
      if (item.queryId === updatedItem.queryId) {
        if (item.validationStatus !== updatedItem.validationStatus) {
          this.announceValidationStatusUpdate(updatedItem);
        }
        return updatedItem;
      }
      return item;
    });
  }

  announceValidationStatusUpdate(newItem: QueryRecord): void {
    const { name: queryName, validationStatus } = newItem;
    let msg = '';
    switch (validationStatus) {
      case Status.SUCCESS: {
        msg = `Validation of Query ${queryName} is completed successfully.`;
        break;
      }

      case Status.FAIL: {
        msg = `Validation of Query ${queryName} is failed.`;
        break;
      }
    }
    this.nzMessageService.info(msg, {
      nzDuration: 5000,
    });
  }

  announceSyntheticUpdate(newItem: QueryRecord): void {
    const { name: queryName, syntheticResult } = newItem;
    let msg = '';
    switch (syntheticResult) {
      case SyntheticResultStatus.IN_PROGRESS: {
        msg = `Execution of Synthetic Query ${queryName} is now in progress.`;
        this.nzMessageService.info(msg, {
          nzDuration: 5000,
        });
        break;
      }

      case SyntheticResultStatus.SUCCESS: {
        msg = `Synthetic Results are ready`;
        this.nzMessageService.success(msg, {
          nzDuration: 5000,
        });
        break;
      }

      case SyntheticResultStatus.FAIL: {
        msg = `Synthetic Results generation failed`;
        this.nzMessageService.error(msg, {
          nzDuration: 5000,
        });
        break;
      }

      case SyntheticResultStatus.CLEANED: {
        msg = `Synthetic Query Result has been cleaned for ${queryName}`;
        this.nzMessageService.info(msg, {
          nzDuration: 5000,
        });
        break;
      }
    }
  }
}
